import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const token = localStorage.getItem('token');

// ADD USER
export const addUser = createAsyncThunk(
    'user/addUser',
    async (userData, { rejectWithValue }) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL || 'https://backend.carbez.com'}/api/employee`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                      },
                    body: JSON.stringify(userData),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                return rejectWithValue(errorData.message || 'User creation failed');
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || 'User creation failed');
        }
    }
);

// FETCH USERS
export const fetchUsers = createAsyncThunk(
    'user/fetchUsers',
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL || 'https://backend.carbez.com'}/api/user`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                      },
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                return rejectWithValue(errorData.message || 'Failed to fetch users');
            }
            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch users');
        }
    }
);

// UPDATE USER
export const updateUser = createAsyncThunk(
    'user/updateUser',
    async ({ id, updatedData }, { rejectWithValue }) => {
        try {
            const baseUrl = process.env.REACT_APP_BASE_URL || 'https://backend.carbez.com';
            const url = `${baseUrl}/api/employee/${id}`;
            const response = await fetch(
                url,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token && { Authorization: `Bearer ${token}` }),
                      },
                    body: JSON.stringify(updatedData),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                return rejectWithValue(errorData.message || 'Failed to update user');
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to update user');
        }
    }
);

// UPDATE USER
export const updateUserFromAdmin = createAsyncThunk(
    'user/updateUserFromAdmin',
    async ({ id, updatedData }, { rejectWithValue }) => {
        try {
            const baseUrl = process.env.REACT_APP_BASE_URL || 'https://backend.carbez.com';
            const url = `${baseUrl}/api/user/update-password/${id}`;
            const response = await fetch(
                url,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token && { Authorization: `Bearer ${token}` }),
                      },
                    body: JSON.stringify(updatedData),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                return rejectWithValue(errorData.message || 'Failed to update user');
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to update user');
        }
    }
);

// UPDATE USER ACTIVENESS
export const updateUserActiveness = createAsyncThunk(
    'user/updateUserActiveness',
    async ({ id, updatedData }, { rejectWithValue }) => {
        try {
            const baseUrl = process.env.REACT_APP_BASE_URL || 'https://backend.carbez.com';
            const url = `${baseUrl}/api/user/update-active/${id}`;
            const response = await fetch(
                url,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token && { Authorization: `Bearer ${token}` }),
                      },
                    body: JSON.stringify(updatedData),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                return rejectWithValue(errorData.message || 'Failed to update user');
            }

            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to update user');
        }
    }
);

const userSlice = createSlice({
    name: 'user',
    initialState: {
        users: [],
        loading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // ADD USER
            .addCase(addUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addUser.fulfilled, (state, action) => {
                state.loading = false;
                state.users.push(action.payload);
            })
            .addCase(addUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // FETCH USERS
            .addCase(fetchUsers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.users = action.payload;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // UPDATE USER
            .addCase(updateUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.loading = false;
                const updatedUser = action.payload;
                const index = state.users.findIndex((user) => user?._id === updatedUser._id);
                if (index !== -1) {
                    state.users[index] = updatedUser;
                }
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // UPDATE USER FROM ADMIN
            .addCase(updateUserFromAdmin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUserFromAdmin.fulfilled, (state, action) => {
                state.loading = false;
                const updatedUser = action.payload;
                if (updatedUser?._id) {
                    const index = state.users.findIndex((user) => user?._id === updatedUser._id);
                    if (index !== -1) {
                        state.users[index] = updatedUser;
                    }
                }
            })
            .addCase(updateUserFromAdmin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // UPDATE USER ACTIVENESS
            .addCase(updateUserActiveness.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUserActiveness.fulfilled, (state, action) => {
                state.loading = false;
                const updatedUser = action.payload;
                const index = state.users.findIndex((user) => user._id === updatedUser._id);
                if (index !== -1) {
                    state.users[index] = updatedUser;
                }
            })
            .addCase(updateUserActiveness.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export default userSlice.reducer;